import {
  Button,
  Checkbox,
  Drawer,
  Input,
  InputNumber,
  Message,
  Modal,
  Pagination,
  Select,
  Space,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import {
  geoRunStatusLabels,
  geoTargetStatusLabels,
  geoTargetTypeOptions,
  geoTargetTypeShortLabels,
  geoPageSizeOptions,
} from "../config";
import { useGeoGovernanceRuntime } from "../runtime/useGeoGovernanceRuntime";
import type { GeoRunStatus, GeoTargetStatus, GeoTargetType } from "../types";
import { canUsePermission } from "../../../lib/auth/ability";

const pageShellStyle = { display: "grid", gap: 16, padding: "4px 0 12px" } as const;
const panelStyle = {
  display: "grid",
  gap: 12,
  padding: 16,
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;
const tableWrapStyle = {
  overflowX: "auto",
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;
const tableStyle = { width: "100%", borderCollapse: "collapse", minWidth: 980 } as const;
const cellStyle = {
  borderBottom: "1px solid #e5e6eb",
  padding: "10px 12px",
  textAlign: "left",
  verticalAlign: "middle",
} as const;
const headerCellStyle = { ...cellStyle, background: "#f7f8fa", color: "#4e5969", fontWeight: 600 } as const;

/** 返回对象类型中文名。 */
function targetTypeLabel(value: string) {
  return geoTargetTypeShortLabels[value as GeoTargetType] ?? value;
}

/** 生成治理对象对应的业务资料编辑入口。 */
function buildBusinessEditPath(target: {
  targetId: string;
  targetType: string;
  targetTable: string;
  orgName?: string | null;
}) {
  const targetId = encodeURIComponent(target.targetId);
  if (target.targetType === "stay_area") return `/dic/stay-areas?geoEditId=${targetId}`;
  if (["resource_point", "airport", "train_station"].includes(target.targetType)) {
    return `/resources/points?geoEditId=${targetId}`;
  }
  if (target.targetType === "school_campus") {
    const orgName = encodeURIComponent(target.orgName ?? "");
    if (target.targetTable === "crm_organization") {
      return `/crm/school-academy-major/school-organizations?geoCreateSite=1&geoOrgName=${orgName}`;
    }
    return `/crm/school-academy-major/school-organizations?geoEditSiteId=${targetId}&geoOrgName=${orgName}`;
  }
  if (target.targetType === "stay_resource") return `/resources/stays/${targetId}?geoEdit=1`;
  return "/data-governance/geo";
}

/** 返回 Arco 状态标签颜色。 */
function statusColor(status: string) {
  if (["completed", "resolved", "verified_no_change"].includes(status)) return "green";
  if (["failed"].includes(status)) return "red";
  if (["review_required", "waiting_review", "skipped"].includes(status)) return "orange";
  return "arcoblue";
}

/** 判断对象是否必须回原业务功能域修正资料。 */
function isBusinessDataIssue(reason?: string | null) {
  return Boolean(reason?.includes("请先修正") && reason.includes("资料"));
}

/** 按批次中最需要人工推进的结果选择对象筛选。 */
function preferredTargetStatus(run: {
  reviewRequiredCount: number;
  failedCount: number;
}): GeoTargetStatus | undefined {
  if (run.reviewRequiredCount > 0) return "review_required";
  if (run.failedCount > 0) return "failed";
  return undefined;
}

/** 历史资源 GEO 批量治理页面。 */
export function GeoGovernanceFeaturePage() {
  const runtime = useGeoGovernanceRuntime();
  const currentUser = useCurrentAuthUser().data;
  const canExecute = canUsePermission(currentUser, "data.geo_governance:execute");
  const runtimeReady = runtime.runtimeStatus?.ready === true;
  const canReview = canUsePermission(currentUser, "data.geo_governance:review");
  const selectedRunIsDryRun = runtime.selectedRun?.dryRun ?? false;
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState<GeoRunStatus>();
  const [createVisible, setCreateVisible] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [targetTypes, setTargetTypes] = useState<GeoTargetType[]>(["stay_resource"]);
  const [dryRun, setDryRun] = useState(true);
  const [targetLimitPerType, setTargetLimitPerType] = useState(20);
  const [executeLimit, setExecuteLimit] = useState(20);
  const [reviewNote, setReviewNote] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>();
  const selectedTarget = runtime.targets.find((target) => target.id === runtime.selectedTargetId);
  const selectedTargetNeedsBusinessFix = isBusinessDataIssue(selectedTarget?.failureReason);
  const selectedTargetHasNoCandidate = Boolean(
    selectedTarget?.status === "review_required"
      && !selectedTargetNeedsBusinessFix
      && !runtime.loadingCandidates
      && runtime.candidates.length === 0,
  );
  const selectedTargetIsTechnicalFailure = Boolean(
    selectedTarget?.status === "failed",
  );
  const selectedCandidate = runtime.candidates.find(
    (candidate) => candidate.id === selectedCandidateId,
  );

  /** 提交新的历史 GEO 治理批次。 */
  async function submitCreate() {
    if (!batchName.trim() || targetTypes.length === 0) {
      Message.warning("请填写批次名称并至少选择一种对象类型");
      return;
    }
    const result = await runtime.createRun({
      batchName,
      targetTypes,
      dryRun,
      targetLimitPerType,
    });
    if (result) {
      setCreateVisible(false);
      setBatchName("");
      Message.success("GEO 治理批次已创建");
    }
  }

  /** 审核当前对象并将选中的候选写回业务模块。 */
  async function approveSelected() {
    if (!runtime.selectedTargetId || !selectedCandidateId) {
      Message.warning("请先选择一个候选");
      return;
    }
    const result = await runtime.approveTarget(
      runtime.selectedTargetId,
      selectedCandidateId,
      reviewNote.trim(),
    );
    if (result) {
      Message.success(selectedRunIsDryRun ? "候选已确认（dry-run 未写回）" : "候选已审核并写回");
      setReviewNote("");
      setSelectedCandidateId(undefined);
      runtime.setSelectedTargetId(undefined);
    }
  }

  /** 跳过当前无法确认的治理对象并保留审核说明。 */
  async function skipSelected() {
    if (!runtime.selectedTargetId) return;
    const result = await runtime.skipTarget(runtime.selectedTargetId, reviewNote.trim());
    if (result) {
      Message.success("对象已跳过并记录审计");
      setReviewNote("");
      runtime.setSelectedTargetId(undefined);
    }
  }

  /** 将无候选对象重新加入当前批次的高德检索队列。 */
  async function retrySelected() {
    if (!runtime.selectedTargetId) return;
    const result = await runtime.retryTarget(runtime.selectedTargetId);
    if (result) {
      Message.success("对象已重新加入检索队列，请点击继续执行");
      setReviewNote("");
      runtime.setSelectedTargetId(undefined);
    }
  }

  /** 从业务主档刷新资料，并在仍缺坐标时立即执行一次核验。 */
  async function refreshSelectedAfterBusinessFix() {
    if (!runtime.selectedTargetId || !runtime.selectedRunId) return;
    const result = await runtime.refreshTargetAfterBusinessFix(
      runtime.selectedTargetId,
      runtime.selectedRunId,
    );
    if (result) {
      Message.success(
        result.status === "verified_no_change" || result.status === "resolved"
          ? "资料已同步，该对象已通过并从待处理列表移除"
          : "资料已同步并重新核验，请查看刷新后的结果",
      );
      setReviewNote("");
      runtime.setSelectedTargetId(undefined);
    }
  }

  return (
    <main style={pageShellStyle}>
      <section style={panelStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "grid", gap: 6 }}>
            <Typography.Title heading={5} style={{ marginBottom: 0 }}>
              GEO 历史治理
            </Typography.Title>
            <Typography.Text type="secondary">
              仅治理经纬度缺失的历史记录。住宿点和非遗不调用 POI；其他实体仅在地理编码无法确认时调用一次 POI。
            </Typography.Text>
          </div>
          <Button type="primary" disabled={!canExecute} onClick={() => setCreateVisible(true)}>
            新建治理批次
          </Button>
        </div>
        <Space wrap>
          <Input
            value={keyword}
            placeholder="按批次名称搜索"
            style={{ width: 220 }}
            onChange={setKeyword}
            onPressEnter={() => runtime.submitRunFilters(keyword, status)}
          />
          <Select
            placeholder="全部批次状态"
            allowClear
            value={status}
            style={{ width: 160 }}
            onChange={(value) => setStatus(value as GeoRunStatus | undefined)}
          >
            {Object.entries(geoRunStatusLabels).map(([value, label]) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
          <Button onClick={() => runtime.submitRunFilters(keyword, status)}>查询</Button>
        </Space>
      </section>

      {runtime.loadingRuntimeStatus ? (
        <section style={panelStyle}>正在检查 GEO 治理运行环境...</section>
      ) : runtimeReady && runtime.runtimeStatus ? (
        <section style={panelStyle}>
          <Space wrap>
            <Tag color="green">运行环境已就绪</Tag>
            <Typography.Text>
              {runtime.runtimeStatus.appEnv} · {runtime.runtimeStatus.llmProvider}
              {runtime.runtimeStatus.llmModel ? ` / ${runtime.runtimeStatus.llmModel}` : ""} · 高德已配置
            </Typography.Text>
          </Space>
        </section>
      ) : (
        <section role="alert" style={{ ...panelStyle, color: "#d03050" }}>
          {runtime.runtimeStatus?.message ?? "无法确认 GEO 治理运行环境，已禁止执行批次。"}
        </section>
      )}

      {runtime.errorMessage ? <section role="alert" style={{ ...panelStyle, color: "#d03050" }}>{runtime.errorMessage}</section> : null}

      <section style={panelStyle}>
        <Typography.Title heading={6} style={{ marginBottom: 0 }}>治理批次</Typography.Title>
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>批次名称</th>
                <th style={headerCellStyle}>对象类型</th>
                <th style={headerCellStyle}>进度</th>
                <th style={headerCellStyle}>结果统计</th>
                <th style={headerCellStyle}>状态</th>
                <th style={headerCellStyle}>操作</th>
              </tr>
            </thead>
            <tbody>
              {runtime.loadingRuns ? <tr><td style={cellStyle} colSpan={6}>正在加载批次...</td></tr> : null}
              {!runtime.loadingRuns && runtime.runs.length === 0 ? <tr><td style={cellStyle} colSpan={6}>暂无治理批次。</td></tr> : null}
              {!runtime.loadingRuns ? runtime.runs.map((run) => (
                <tr key={run.id}>
                  <td style={cellStyle}>{run.batchName}</td>
                  <td style={cellStyle}>{run.targetTypes.map(targetTypeLabel).join("、")}</td>
                  <td style={cellStyle}>{run.processedCount} / {run.totalCount}</td>
                  <td style={cellStyle}>
                    通过 {run.resolvedCount}；待人工处理 {run.reviewRequiredCount}；失败 {run.failedCount}
                  </td>
                  <td style={cellStyle}><Tag color={statusColor(run.status)}>{geoRunStatusLabels[run.status]}</Tag>{run.dryRun ? <Tag color="arcoblue">仅候选</Tag> : null}</td>
                  <td style={cellStyle}>
                    <Button
                      type="text"
                      onClick={() => {
                        runtime.setSelectedRunId(run.id);
                        runtime.setTargetStatus(preferredTargetStatus(run));
                      }}
                    >
                      查看详情
                    </Button>
                  </td>
                </tr>
              )) : null}
            </tbody>
          </table>
        </div>
        <Pagination
          current={runtime.runFilters.page}
          pageSize={runtime.runFilters.pageSize}
          total={runtime.runsTotal}
          sizeCanChange
          sizeOptions={geoPageSizeOptions}
          onChange={runtime.setRunPage}
          onPageSizeChange={runtime.setRunPageSize}
          showTotal
        />
      </section>

      {runtime.selectedRun ? (
        <section style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 4 }}>
              <Typography.Title heading={6} style={{ marginBottom: 0 }}>批次详情：{runtime.selectedRun.batchName}</Typography.Title>
              <Typography.Text type="secondary">批次 ID：{runtime.selectedRun.id}</Typography.Text>
            </div>
            <Space>
              <Typography.Text>每次</Typography.Text>
              <InputNumber
                aria-label="每次执行条数"
                value={executeLimit}
                min={1}
                max={100}
                step={10}
                precision={0}
                mode="button"
                style={{ width: 132 }}
                onChange={(value) => setExecuteLimit(value)}
              />
              <Typography.Text>条</Typography.Text>
              <Button
                type="primary"
                disabled={!canExecute || !runtimeReady || runtime.selectedRun.status === "completed" || runtime.selectedRun.status === "failed"}
                loading={runtime.executing}
                onClick={() => void runtime.executeRun(runtime.selectedRun!.id, executeLimit)}
              >
                继续执行
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Tag color={statusColor(runtime.selectedRun.status)}>
              {runtime.executing ? "本次执行中" : geoRunStatusLabels[runtime.selectedRun.status]}
            </Tag>
            <Tag>总数 {runtime.selectedRun.totalCount}</Tag>
            <Tag color="arcoblue">已处理 {runtime.selectedRun.processedCount} / {runtime.selectedRun.totalCount}</Tag>
            <Tag>待执行 {Math.max(runtime.selectedRun.totalCount - runtime.selectedRun.processedCount, 0)}</Tag>
            <Tag color="green">通过 {runtime.selectedRun.resolvedCount}</Tag>
            <Tag color="orange">待人工处理 {runtime.selectedRun.reviewRequiredCount}</Tag>
            <Tag color="red">失败 {runtime.selectedRun.failedCount}</Tag>
          </Space>
          <Space>
            <Select
              aria-label="对象类型筛选"
              value={runtime.targetFilters.targetType}
              allowClear
              placeholder="全部对象类型"
              style={{ width: 160 }}
              onChange={(value) => runtime.setTargetType(value as GeoTargetType | undefined)}
            >
              {geoTargetTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {geoTargetTypeShortLabels[option.value]}
                </Select.Option>
              ))}
            </Select>
            <Select
              value={runtime.targetFilters.status}
              allowClear
              placeholder="对象状态"
              style={{ width: 160 }}
              onChange={(value) => runtime.setTargetStatus(value as GeoTargetStatus | undefined)}
            >
              {Object.entries(geoTargetStatusLabels).map(([value, label]) => (
                <Select.Option key={value} value={value}>{label}</Select.Option>
              ))}
            </Select>
            <Typography.Text type="secondary">
              {runtime.targetFilters.status === "failed"
                ? "当前查看技术失败对象；可逐条重试，重新加入批次后再继续执行。"
                : runtime.targetFilters.status === "review_required"
                  ? "当前查看待人工处理对象；有候选时人工审核，无候选时可重新检索，资料待修正可直达业务页。"
                  : "当前查看全部对象，可按状态筛选需要处理的记录。"}
            </Typography.Text>
          </Space>
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead><tr><th style={headerCellStyle}>对象</th><th style={headerCellStyle}>类型</th><th style={headerCellStyle}>原始行政区/地址</th><th style={headerCellStyle}>原坐标</th><th style={headerCellStyle}>状态</th><th style={headerCellStyle}>操作</th></tr></thead>
              <tbody>
                {runtime.loadingTargets ? <tr><td style={cellStyle} colSpan={6}>正在加载治理对象...</td></tr> : null}
                {!runtime.loadingTargets && runtime.targets.length === 0 ? <tr><td style={cellStyle} colSpan={6}>暂无符合条件的对象。</td></tr> : null}
                {!runtime.loadingTargets ? runtime.targets.map((target) => (
                  <tr key={target.id}>
                    <td style={cellStyle}><div>{target.targetName}</div><Typography.Text type="secondary">{target.targetTable} / {target.targetId}</Typography.Text></td>
                    <td style={cellStyle}>{targetTypeLabel(target.targetType)}</td>
                    <td style={cellStyle}>{[target.province, target.city, target.district].filter(Boolean).join(" ")}<br />{target.address || "未提供地址"}</td>
                    <td style={cellStyle}>{target.lng != null && target.lat != null ? `${target.lng}, ${target.lat}` : "无"}</td>
                    <td style={cellStyle}><Tag color={statusColor(target.status)}>{target.status === "review_required" ? (isBusinessDataIssue(target.failureReason) ? "资料待修正" : target.candidateCount === 0 ? "无候选" : "待审核") : geoTargetStatusLabels[target.status]}</Tag>{target.failureReason && target.failureReason !== "无候选" ? <div style={{ color: "#86909c" }}>{target.failureReason}</div> : null}</td>
                    <td style={cellStyle}><Space><Button onClick={() => { runtime.setSelectedTargetId(target.id); setSelectedCandidateId(undefined); }}>{target.status === "failed" ? "查看失败详情" : target.status === "review_required" && target.candidateCount === 0 ? "处理" : "查看候选"}</Button>{target.status === "failed" ? <Button type="text" disabled={!canExecute} loading={runtime.reviewingTargetId === target.id} onClick={() => void runtime.retryTarget(target.id)}>重试</Button> : null}</Space></td>
                  </tr>
                )) : null}
              </tbody>
            </table>
          </div>
          <Pagination current={runtime.targetFilters.page} pageSize={runtime.targetFilters.pageSize} total={runtime.targetsTotal} sizeCanChange sizeOptions={geoPageSizeOptions} onChange={runtime.setTargetPage} onPageSizeChange={runtime.setTargetPageSize} showTotal />
        </section>
      ) : null}

      <Modal title="新建 GEO 治理批次" visible={createVisible} onCancel={() => setCreateVisible(false)} onOk={() => void submitCreate()} confirmLoading={runtime.creating} autoFocus={false}>
        <div style={{ display: "grid", gap: 14 }}>
          <Input placeholder="批次名称" value={batchName} onChange={setBatchName} />
          <div style={{ display: "grid", gap: 8 }}>
            <Typography.Text>治理对象类型</Typography.Text>
            <Space wrap>
              <Button size="small" onClick={() => setTargetTypes(geoTargetTypeOptions.map((item) => item.value))}>
                选择全部六类
              </Button>
              <Button size="small" type="text" onClick={() => setTargetTypes([])}>
                清空
              </Button>
            </Space>
            <div style={{ display: "grid", gap: 8 }}>
              {geoTargetTypeOptions.map((option) => (
                <label key={option.value} style={{ display: "grid", gap: 2 }}>
                  <Checkbox
                    checked={targetTypes.includes(option.value)}
                    onChange={(checked) => setTargetTypes((current) => (
                      checked
                        ? [...new Set([...current, option.value])]
                        : current.filter((item) => item !== option.value)
                    ))}
                  >
                    {option.label}
                  </Checkbox>
                  <Typography.Text type="secondary" style={{ paddingLeft: 24 }}>
                    {option.description}
                  </Typography.Text>
                </label>
              ))}
            </div>
          </div>
          <Checkbox checked={dryRun} onChange={setDryRun}>仅生成候选（推荐先试跑）</Checkbox>
          <InputNumber
            aria-label="每种对象最多条数"
            value={targetLimitPerType}
            min={1}
            max={100}
            precision={0}
            mode="button"
            style={{ width: 180 }}
            onChange={(value) => setTargetLimitPerType(value ?? 20)}
          />
          <Typography.Text type="secondary">每种对象最多冻结 1 至 100 条，默认 20 条，便于先做小批量 dry-run。</Typography.Text>
          <Typography.Text type="secondary">正式写回需新建 dry_run=false 批次；治理页面不会调用业务域单条 API。</Typography.Text>
        </div>
      </Modal>

      <Drawer title="候选与处理" width={760} visible={Boolean(runtime.selectedTargetId)} onCancel={() => runtime.setSelectedTargetId(undefined)} footer={null}>
        <div style={{ display: "grid", gap: 16 }}>
          {selectedTarget ? (
            <div style={{ display: "grid", gap: 4 }}>
              {selectedTarget.orgName ? <Typography.Text>学校主体：{selectedTarget.orgName}</Typography.Text> : null}
              {selectedTarget.contextName ? <Typography.Text type="secondary">核验上下文：{selectedTarget.contextName}</Typography.Text> : null}
            </div>
          ) : null}
          {runtime.loadingCandidates ? <Typography.Text>正在加载 GEO 候选...</Typography.Text> : null}
          {!runtime.loadingCandidates && runtime.candidates.length === 0 ? <Typography.Text type="warning">{selectedTargetNeedsBusinessFix && selectedTarget ? `请先在${targetTypeLabel(selectedTarget.targetType)}业务页补齐资料，保存后回到这里立即核验。` : selectedTargetIsTechnicalFailure ? "技术调用失败，可重试；不要跳过技术失败对象。" : "本次没有可供审核的候选。可以重新检索，或确认资料不足后跳过并记录原因。"}</Typography.Text> : null}
          {selectedTargetNeedsBusinessFix && selectedTarget ? (
            <Space wrap>
              <Button
                type="primary"
                href={buildBusinessEditPath(selectedTarget)}
                target="_blank"
                rel="noreferrer"
              >
                去{targetTypeLabel(selectedTarget.targetType)}业务页完善资料
              </Button>
              <Button
                disabled={!canExecute}
                loading={runtime.reviewingTargetId === runtime.selectedTargetId}
                onClick={() => void refreshSelectedAfterBusinessFix()}
              >
                资料已完善，立即核验
              </Button>
            </Space>
          ) : null}
          {runtime.candidates.map((candidate) => (
            <label key={candidate.id} style={{ display: "grid", gap: 4, padding: 12, border: selectedCandidateId === candidate.id ? "2px solid #165dff" : "1px solid #e5e6eb", borderRadius: 6, cursor: "pointer" }}>
              <span><input type="radio" name="geo-candidate" disabled={selectedTargetIsTechnicalFailure} checked={selectedCandidateId === candidate.id} onChange={() => setSelectedCandidateId(candidate.id)} /> {candidate.candidateName}</span>
              {candidate.provider === "amap_geocode" ? (
                <>
                  <Space wrap>
                    <Tag color="arcoblue">
                      {selectedTarget?.targetType === "stay_resource" ? "LLM 经营地址" : "LLM 定位地址"}
                    </Tag>
                    <Typography.Text>{candidate.searchTerm}</Typography.Text>
                  </Space>
                  <Space wrap><Tag color="green">高德地理编码</Tag><Typography.Text type="secondary">标准地址：{candidate.address}</Typography.Text></Space>
                  <Typography.Text type="secondary">行政区：{candidate.province} {candidate.city} {candidate.district}；坐标：{candidate.lng}, {candidate.lat}（{candidate.coordinateSystem}）</Typography.Text>
                </>
              ) : (
                <>
                  <Typography.Text type="secondary">{candidate.province} {candidate.city} {candidate.district} · {candidate.address}</Typography.Text>
                  <Typography.Text type="secondary">POI：{candidate.poiId}；坐标：{candidate.lng}, {candidate.lat}（{candidate.coordinateSystem}）</Typography.Text>
                </>
              )}
              <Typography.Text type="secondary">判定：{candidate.qualificationReason}</Typography.Text>
            </label>
          ))}
          {runtime.audits.length > 0 ? <Typography.Text type="secondary">最近审计：{runtime.audits[0].reviewAction} / {runtime.audits[0].reviewNote}</Typography.Text> : null}
          {!selectedTargetNeedsBusinessFix ? <Input.TextArea placeholder="审核说明（跳过时建议填写无候选等原因）" value={reviewNote} maxLength={1000} onChange={setReviewNote} /> : null}
          <Space>
            {runtime.candidates.length > 0 && !selectedTargetIsTechnicalFailure ? <Button type="primary" disabled={!canReview || !selectedCandidate} loading={runtime.reviewingTargetId === runtime.selectedTargetId} onClick={() => void approveSelected()}>{selectedRunIsDryRun ? "确认候选（不写回）" : "选择候选并写回"}</Button> : null}
            {selectedTargetHasNoCandidate || selectedTargetIsTechnicalFailure ? <Button type="primary" disabled={!canExecute} loading={runtime.reviewingTargetId === runtime.selectedTargetId} onClick={() => void retrySelected()}>重新检索</Button> : null}
            {!selectedTargetIsTechnicalFailure && !selectedTargetNeedsBusinessFix ? <Button disabled={!canReview} loading={runtime.reviewingTargetId === runtime.selectedTargetId} onClick={() => void skipSelected()}>跳过</Button> : null}
          </Space>
        </div>
      </Drawer>
    </main>
  );
}
